import React, {
  createElement,
  useRef,
  forwardRef,
  useEffect,
  useCallback,
  useState,
  useLayoutEffect,
  useMemo,
  useImperativeHandle,
} from 'react';
import { TreeProps, FlatTreeNode, Node } from './';
import { TreeNode } from './TreeNode';
import { DraggableTreeNode } from './dnd/DraggableTreeNode';
import { TreeNodeDragPreview } from './dnd/TreeNodeDragPreview';
import { DragSourceMonitor } from 'react-dnd';
import { DesignTokens, renderInlineCssVars, useDesignTokens } from '../../theme';
import { useRefArray } from '../../hooks/useRefArray';
import { postfixTestId } from '../../util/testId';
import { ButtonVariant } from '../Button';
import { Toolbar, ToolbarItem } from '../Toolbar';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import bundle from './NovaComponents-Tree-gui-icu.properties';
import { useTreeNavigation } from './hooks/useTreeNavigation';
import { useResizeObserverRef } from '../../hooks/useResizeObserver';
import { ShowAll, HideAll } from '@sas/react-svg';
import { createUseHtmlId } from '../../hooks/useHtmlId';
import {
  KeyboardAction,
  TreeNodeNavigationKeys,
  useTreeNodeKeyboardHandler,
} from './hooks/useTreeNodeKeyboardHandler';
import { InvisibleText } from '../InvisibleText/InvisibleText';
import { setRef } from '../../util/ref-util';

import cx from 'classnames';
import cn from './Tree.css.json';
import './Tree.css';

const useTextLocalizer = createUseTextLocalizer(bundle);
const generateTreeDescriptionId = createUseHtmlId('tree-description-header');

export function byId(nodes: Node[], id: string): Node | null {
  function get(nodes: Node[] | undefined, id: string): Node | null {
    if (!nodes) return null;
    const potentialMatch = nodes.find((n) => n.id === id);
    if (potentialMatch) return potentialMatch;
    for (const node of nodes) {
      const nextNodes = get(node.children, id);
      if (nextNodes) return nextNodes;
    }
    return null;
  }
  return get(nodes, id);
}

export const Tree = forwardRef<HTMLDivElement, TreeProps>((props, ref) => {
  const localize = useTextLocalizer();
  const useTreeDndPrefixId = createUseHtmlId('Tree-dndPrefix');
  const defaultDndPrefix = useTreeDndPrefixId();
  const dndPrefix = props.dndPrefix ?? defaultDndPrefix;
  const dragType = props.dragType ?? 'treeNode';

  const allowDrag =
    props.allowDrag ??
    function (_: Node) {
      return true;
    };

  const [isDragging, setDragging] = useState(false);

  const flatData = props.nodes;

  // Most components like Settings need this feature only for initial rendering hence being worked out
  function getSeedFocusIndex() {
    const selectedNodes = props.selectedNodes ?? {};
    const selectedNodeKeys = Object.keys(selectedNodes);
    const sortedSelectedNodeIndices = selectedNodeKeys
      .map((selectedNodeValue) =>
        flatData.findIndex((flatdataValue) => flatdataValue.node.id === selectedNodeValue)
      )
      .sort();
    return sortedSelectedNodeIndices.length > 0
      ? sortedSelectedNodeIndices[0] > -1
        ? sortedSelectedNodeIndices[0]
        : 0
      : 0;
  }
  // But few components like ContentSelector had to render the selected nodes after intialization.
  const lazyLoadingRef = useRef<boolean>(flatData.length === 0);

  const [seedFocusindex] = useState(() => getSeedFocusIndex());
  useEffect(() => {
    const seedFocusIndex = getSeedFocusIndex();
    if (lazyLoadingRef.current && seedFocusIndex) {
      setCurrentFocusIndex(seedFocusIndex);
      // change the focused index when there was no nodes initially and the selection is not the first node
      lazyLoadingRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flatData, props.selectedNodes]); // update the seed focus index when the flat nodes or selected node are changed.

  // current focus index is maintained as a Ref and not as a state since
  // we do not want un-necessary re-render while navigating visible nodes.
  const currentFocusedIndexRef = useRef<number>(seedFocusindex);
  const getCurrentFocusIndex = () => currentFocusedIndexRef.current;
  const setCurrentFocusIndex = (value: number) => (currentFocusedIndexRef.current = value);

  // for scroll induced by keyboard navigator actions
  const isProgrammaticScrolledRef = useRef(false);

  // for specific use case of performing keyboard action on a node in focus but out of view.
  const postFocusKeyboardActionRef = useRef<KeyboardAction | undefined>();

  // need a ref to clear tabindex
  const focusedNode = useRef<HTMLElement | null>();

  const treeContainerRef = useRef<HTMLDivElement | null>(null);

  const { refs, createSetRef } = useRefArray<HTMLElement>();

  const isMouseDownRef = useRef(false);

  // detect scroll awareness
  const [scrollTop, setScrollTop] = useState(0);
  const onScroll = useCallback(() => {
    const treeContainerDom = treeContainerRef?.current;
    if (treeContainerDom) {
      setScrollTop(treeContainerDom.scrollTop);
      if (!isDragging && isMouseDownRef.current) {
        focusedNode.current?.setAttribute('tabindex', '-1');
        treeContainerDom.setAttribute('tabindex', '0');
        treeContainerDom.focus();
      }
    }
  }, [isDragging]);

  useEffect(() => {
    const treeContainerDom = treeContainerRef?.current;
    if (treeContainerDom) {
      treeContainerDom.addEventListener('scroll', onScroll);

      return () => {
        treeContainerDom.removeEventListener('scroll', onScroll);
      };
    }
  }, [onScroll]);

  // defaults only come into play during tests execution.
  const [treeHeight, setTreeHeight] = useState(500);
  const [nodeHeight, setNodeHeight] = useState(24);

  const _updateHeight = useCallback(() => {
    if (!isDragging) {
      const treeDom = treeContainerRef.current;
      const nodeDom = treeDom?.querySelector('li');

      if (treeDom && treeDom.getBoundingClientRect().height > 0) {
        setTreeHeight(treeDom.getBoundingClientRect().height);
      }
      if (nodeDom && nodeDom.getBoundingClientRect().height > 0) {
        setNodeHeight(nodeDom.getBoundingClientRect().height);
      }
    }
  }, [isDragging]);

  // to prevent vertical scrollbar flicker when the available height is less than the default height.
  useLayoutEffect(() => {
    _updateHeight();
  }, [_updateHeight]);

  const [setTreeReSizeRef] = useResizeObserverRef<HTMLElement>(_updateHeight);

  // page calculation
  const bufferNodes = 5;
  const nodeCount = flatData.length;
  const totalHeight = Math.max(nodeCount * nodeHeight, treeHeight);
  const maxScrollTop = Math.max(0, Math.round(totalHeight - treeHeight));

  // first rendered node index in the displayed page
  const startNodeIndex = Math.max(
    0,
    Math.floor(Math.min(scrollTop, maxScrollTop) / nodeHeight) - bufferNodes
  );

  // total rendered row count in the displayed page
  const renderedNodeCount = Math.min(
    nodeCount - startNodeIndex,
    Math.ceil(treeHeight / nodeHeight) + 2 * bufferNodes
  );

  if (getCurrentFocusIndex() > nodeCount - 1) {
    setCurrentFocusIndex(0);
  }

  const focusTreeNode = (index: number) => {
    refs[index]?.focus();
    if (postFocusKeyboardActionRef.current) {
      const keyboardAction = postFocusKeyboardActionRef.current;
      postFocusKeyboardActionRef.current = undefined;
      keyboardAction && onKeyboardAction(getCurrentFocusIndex(), keyboardAction);
    }
  };

  const setProgrammaticScrollTop = (value: number) => {
    isProgrammaticScrolledRef.current = true;
    const treeContainerDom = treeContainerRef.current;
    if (treeContainerDom) {
      treeContainerDom.scrollTop = value;
    }
  };

  const navigator = useTreeNavigation(
    { treeHeight, nodeHeight, nodeCount },
    {
      scrollTop,
      setScrollTop: setProgrammaticScrollTop,
      startNodeIndex,
      renderedNodeCount,
    },
    {
      getCurrentFocusIndex,
      setCurrentFocusIndex,
      focusTreeNode,
    }
  );

  useImperativeHandle(
    props.treeRef,
    () => ({
      scrollToNode(index) {
        navigator.scrollToIndex(index);
      },
      getScrollTop() {
        return treeContainerRef.current?.scrollTop ?? 0;
      },
      setScrollTop(value) {
        setProgrammaticScrollTop(value);
      },
    }),
    [navigator]
  );

  const onKeyboardAction = useTreeNodeKeyboardHandler(flatData, props, navigator);

  // the re-render is triggered on both interactive or programmatic scrolling.
  useEffect(() => {
    const indexToFocus = getCurrentFocusIndex() - startNodeIndex;
    if (isProgrammaticScrolledRef.current && refs[indexToFocus]) {
      isProgrammaticScrolledRef.current = false;
      focusTreeNode(indexToFocus);
    }
  });

  function onArrowClick(id: string) {
    const targetNode = flatData.find((item) => item.node.id === id)?.node;
    if (targetNode) {
      const isExpanded = props.expandedNodes[id] !== true; // Flip the expanded state of this node
      props.onExpand(targetNode, isExpanded);
    }
  }

  function scrollFocusNodeNodeIntoViewIfClipped() {
    if (treeContainerRef.current && focusedNode.current) {
      const treeContainerDom = treeContainerRef.current;
      const focusedNodeDom = focusedNode.current;
      if (
        focusedNodeDom.getBoundingClientRect().bottom >
        treeContainerDom.getBoundingClientRect().bottom
      ) {
        setCurrentFocusIndex(getCurrentFocusIndex() - 1);
        navigator.down();
      } else if (
        focusedNodeDom.getBoundingClientRect().top < treeContainerDom.getBoundingClientRect().top
      ) {
        setCurrentFocusIndex(getCurrentFocusIndex() + 1);
        navigator.up();
      }
    }
  }

  function getTop(rowIndex: number): number {
    let top = 0;
    if (totalHeight > treeHeight) {
      top = startNodeIndex * nodeHeight + nodeHeight * rowIndex;
    } else {
      top = nodeHeight * rowIndex;
    }
    return top;
  }

  const dragStart = () => {
    setDragging(true);
  };

  const dragEnd = (draggedItem: any, monitor: DragSourceMonitor) => {
    setDragging(false);
    props.onDragEnd?.(draggedItem, monitor);
  };

  const createTreeNodeWithDnd = (flatNode: FlatTreeNode, nodeIndex: number, rowIndex: number) => {
    return (
      <DraggableTreeNode
        dndPrefix={dndPrefix}
        dragType={dragType}
        allowDrag={props.allowDrag?.(flatNode.node)}
        id={flatNode.node.id}
        node={flatNode.node}
        onDragStart={dragStart}
        onDragEnd={dragEnd}
      >
        {createTreeNode(flatNode, nodeIndex, rowIndex)}
      </DraggableTreeNode>
    );
  };

  const createTreeNode = (flatNode: FlatTreeNode, nodeIndex: number, rowIndex: number) => {
    const { node, depth, parentId, siblingCount, siblingPosition } = flatNode;
    const isExpanded = props.expandedNodes[node.id] === true;
    const isSelected = (props.selectedNodes && props.selectedNodes[node.id]) ?? false;
    return (
      <li
        key={node.id}
        role="presentation"
        style={{
          position: 'absolute',
          top: `${getTop(rowIndex)}px`,
          width: '100%',
        }}
      >
        <TreeNode
          depth={depth}
          siblingCount={siblingCount}
          siblingPosition={siblingPosition}
          node={node}
          testId={node.testId ?? postfixTestId(props.testId, node.id)}
          selected={isSelected}
          expanded={isExpanded}
          onArrowClick={onArrowClick}
          ref={createSetRef(rowIndex)}
          isChildNode={!!parentId}
          hasChildren={Array.isArray(node.children) && node.children.length > 0}
          onDoubleClick={(targetNode) => {
            targetNode.isActionable && props.onActivate?.(targetNode);
          }}
          onSelect={(targetNode, eventData) => {
            if (props.onSelect) {
              if (targetNode) props.onSelect(targetNode, eventData);
            }
            scrollFocusNodeNodeIntoViewIfClipped();
          }}
          tabIndex={getCurrentFocusIndex() === nodeIndex ? 0 : -1}
          onFocus={(event) => {
            setCurrentFocusIndex(nodeIndex);
            focusedNode.current?.setAttribute('tabindex', '-1');
            event.target.setAttribute('tabindex', '0');
            focusedNode.current = refs[refs.indexOf(event.currentTarget as HTMLDivElement)];
          }}
          onContext={!node.disabled ? props.onContextMenu : undefined}
          onKeyDown={(event) => {
            onKeyboardAction(nodeIndex, {
              keyPressed: event.key,
              ctrlKey: event.ctrlKey,
              shiftKey: event.shiftKey,
              metaKey: event.metaKey,
            });
            if (
              Object.values(TreeNodeNavigationKeys).includes(event.key as TreeNodeNavigationKeys)
            ) {
              event.preventDefault();
            }
          }}
        />
      </li>
    );
  };

  const dt = useDesignTokens();
  const tokens = renderInlineCssVars(getTreeTokens(dt), 'tree');

  const items: ToolbarItem[] = [];

  const titlelblId = props.id ? `${props.id}-title-lbl` : 'title-lbl';
  const isTitleExist = props.title && props.title.trim().length > 0;

  isTitleExist &&
    items.push({
      id: titlelblId,
      type: 'Heading',
      testId: postfixTestId(props.testId, 'titleLbl'),
      level: 4,
      className: cn.title,
      children: props.title,
    });

  (isTitleExist || props.showExpandCollapse === true) &&
    items.push({
      type: 'ToolbarSpacer',
    });

  props.showExpandCollapse === true &&
    items.push(
      {
        type: 'Button',
        variant: ButtonVariant.Icon,
        icon: { icon: HideAll },
        testId: postfixTestId(props.testId, 'collapseAllBtn'),
        ariaLabel: localize('hideall.button.aria'),
        onPress: () => {
          setCurrentFocusIndex(0);
          startNodeIndex === 0 && refs[0]?.setAttribute('tabindex', '0');
          props.onCollapseAll && props.onCollapseAll();
        },
      },
      {
        type: 'Button',
        variant: ButtonVariant.Icon,
        icon: { icon: ShowAll },
        testId: postfixTestId(props.testId, 'expandAllBtn'),
        ariaLabel: localize('showall.button.aria'),
        onPress: () => {
          setCurrentFocusIndex(0);
          startNodeIndex === 0 && refs[0]?.setAttribute('tabindex', '0');
          props.onExpandAll && props.onExpandAll();
        },
      }
    );

  const selectedNodeKeys = Object.keys(props.selectedNodes ?? {});
  const draggableSelectedNodeCount =
    selectedNodeKeys.length > 0
      ? flatData.filter(
          (flatNode) => selectedNodeKeys.indexOf(flatNode.node.id) > -1 && allowDrag(flatNode.node)
        ).length
      : 0;

  const getTreeNodesContainer = () => (
    <div
      className={cn.nodesContainer}
      style={{ overflow: 'auto' }}
      role={props.role}
      data-landmark-label={props.landmarkLabel}
      aria-label={props.nodesContainerAriaLabel}
      ref={(ele) => {
        treeContainerRef.current = ele;
        setTreeReSizeRef(ele);
        setRef(ref, ele);
      }}
      tabIndex={0}
      onMouseDown={() => (isMouseDownRef.current = true)}
      onMouseUp={() => (isMouseDownRef.current = false)}
      onFocus={(e) => {
        treeContainerRef.current?.setAttribute('tabindex', '-1');
        if (e.target === e.currentTarget && isMouseDownRef.current === false) {
          navigator.scrollToFocusedNode();
          if (lazyLoadingRef.current && flatData.length > 0) lazyLoadingRef.current = false;
          // If the tree is focused with loaded nodes, set the flag to false in case no lazy selection node id
        }
      }}
      onKeyDown={(event: React.KeyboardEvent) => {
        if (
          event.target === event.currentTarget &&
          Object.values(TreeNodeNavigationKeys).includes(event.key as TreeNodeNavigationKeys)
        ) {
          const eventData = {
            keyPressed: event.key,
            ctrlKey: event.ctrlKey,
            shiftKey: event.shiftKey,
            metaKey: event.metaKey,
          };
          focusedNode.current?.setAttribute('tabindex', '-1');
          treeContainerRef.current?.setAttribute('tabindex', '-1');

          postFocusKeyboardActionRef.current = eventData;
          navigator.scrollToFocusedNode();

          event.preventDefault();
        }
      }}
    >
      <ul
        role="tree"
        className={cn.nodes}
        style={{
          position: 'relative',
          overflow: 'hidden',
          height: totalHeight,
        }}
        {...(ariaLabelledByAttr && { 'aria-labelledby': ariaLabelledByAttr })}
        aria-label={props.ariaLabel}
        {...(ariaDescribedByAttr && { 'aria-describedby': ariaDescribedByAttr })}
      >
        {new Array(renderedNodeCount)
          .fill(null)
          .map((_, index) =>
            flatData[index + startNodeIndex]
              ? props.allowDrag?.(flatData[index + startNodeIndex].node)
                ? createTreeNodeWithDnd(
                    flatData[index + startNodeIndex],
                    index + startNodeIndex,
                    index
                  )
                : createTreeNode(flatData[index + startNodeIndex], index + startNodeIndex, index)
              : null
          )}
      </ul>
      {props.allowDrag ? (
        <TreeNodeDragPreview
          dndPrefix={dndPrefix}
          dragType="treeNode"
          draggedNodeCount={draggableSelectedNodeCount}
        />
      ) : null}
    </div>
  );

  const defaultAriaDescribedBy = useMemo(() => {
    let treeNodehasActionableProperty = false,
      treeNodehasDeletableProperty = false;
    for (const item of props.nodes) {
      if (item.node.isActionable) {
        treeNodehasActionableProperty = true;
      }
      if (item.node.isDeletable) {
        treeNodehasDeletableProperty = true;
      }
      if (treeNodehasActionableProperty && treeNodehasDeletableProperty) {
        break;
      }
    }

    if (treeNodehasActionableProperty && treeNodehasDeletableProperty) {
      return localize('tree.activateAndDeleteKeys.msg');
    } else if (treeNodehasActionableProperty) {
      return localize('tree.activateUsingEnterKey.msg');
    } else if (treeNodehasDeletableProperty) {
      return localize('tree.deleteUsingDeleteKey.msg');
    }

    return '';
  }, [localize, props.nodes]);

  const treeAriaDescribedBy = generateTreeDescriptionId();

  let ariaLabelledByAttr = '';
  if (isTitleExist) {
    ariaLabelledByAttr = titlelblId;
  }
  if (props.ariaLabelledBy) {
    ariaLabelledByAttr = ariaLabelledByAttr.concat(` ${props.ariaLabelledBy}`).trimLeft();
  }

  let ariaDescribedByAttr = '';
  if (props.ariaDescribedBy) {
    ariaDescribedByAttr = props.ariaDescribedBy;
  }
  // related to TreeNode actions
  if (defaultAriaDescribedBy) {
    ariaDescribedByAttr = ariaDescribedByAttr.concat(` ${treeAriaDescribedBy}`).trimLeft();
  }

  return (
    <div
      className={cx(cn.container, props.className)}
      style={{ ...tokens, ...props.style }}
      data-testid={props.testId}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as HTMLElement)) {
          treeContainerRef.current?.setAttribute('tabindex', '0');
        }
      }}
    >
      {defaultAriaDescribedBy !== '' ? (
        <InvisibleText testId="tree-description" id={treeAriaDescribedBy}>
          {defaultAriaDescribedBy}
        </InvisibleText>
      ) : null}
      {(props.title && props.title.trim().length >= 0) || props.showExpandCollapse === true ? (
        <Toolbar items={items} />
      ) : null}
      {getTreeNodesContainer()}
    </div>
  );
});

export const getTreeTokens = (dt: DesignTokens) => {
  return {
    fg: dt.c.colorTextDefault,
    'hover-bg': dt.stateStandardContentNova.hover.bg,
    'selected-bg': dt.stateStandardContentNova.selected.bg,
    'selected-fg': dt.c.colorTextDefault,
    'selected-filter': dt.stateStandardContentNova.selected.filter,
    'disabled-fg': dt.stateStandardContentNova.disabled.fg,
    'spacing-xxs': dt.spacing.core.xxs,
    'spacing-xs': dt.spacing.core.xs,
    'spacing-s': dt.spacing.core.xs,
    'font-size-m': dt.fontNova.size.m,
    'font-weight-thick': dt.fontNova.weight.thick,
  };
};
